import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import Sidebar from './Sidebar';
import Header from './Header';
import ProfileTable from './ProfileTable';
import ProfileDetailModal from './ProfileDetailModal';
import { getKycProfiles } from '../../apis/checkin.api';
import { useAuth } from '../../contexts/auth.context';

const ProfileManagement = () => {
  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    profileTypes: ['primary', 'secondary'],
    status: ['COMPLETED', 'NEEDS_REVIEW', 'DECLINED'],
  });
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchProfiles = async authToken => {
    try {
      setLoading(true);
      setError(null);
      const data = await getKycProfiles(authToken.current);
      // Sort profiles by modifiedAt in descending order (newest first)
      const sortedData = [...data].sort((a, b) => new Date(b.modifiedAt) - new Date(a.modifiedAt));
      setProfiles(sortedData);
    } catch (err) {
      setError('Failed to fetch profiles. Please try again later.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles(authToken);
  }, [authToken]);

  const handleFilterChange = newFilters => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleRefresh = () => {
    fetchProfiles(authToken);
  };

  const handleViewProfile = profile => {
    setSelectedProfile(profile);
    setShowDetailModal(true);
  };

  const getFilteredProfiles = () => {
    return profiles.filter(profile => {
      // Filter by search term
      const searchTerm = filters.search.toLowerCase();
      if (
        searchTerm &&
        !profile.id.toLowerCase().includes(searchTerm) &&
        !profile.walletId.toLowerCase().includes(searchTerm) &&
        !profile.primaryWalletId.toLowerCase().includes(searchTerm)
      ) {
        return false;
      }

      // Filter by profile type
      const profileType = profile.isPrimary ? 'primary' : 'secondary';
      if (!filters.profileTypes.includes(profileType)) {
        return false;
      }

      // Filter by status
      if (!filters.status.includes(profile.status)) {
        return false;
      }

      // Filter by start date
      if (filters.startDate) {
        const profileDate = DateTime.fromISO(profile.createdAt);
        const startDate = DateTime.fromISO(filters.startDate);
        if (profileDate < startDate) {
          return false;
        }
      }

      return true;
    });
  };

  if (loading) {
    return (
      <Container fluid className="h-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  const filteredProfiles = getFilteredProfiles();

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col xs={12} md={3} lg={2} className="sidebar bg-light p-0">
          <Sidebar filters={filters} onFilterChange={handleFilterChange} />
        </Col>
        <Col xs={12} md={9} lg={10} className="main-content p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          <Header onRefresh={handleRefresh} profiles={filteredProfiles} />
          <ProfileTable profiles={filteredProfiles} onViewProfile={handleViewProfile} />
          <ProfileDetailModal
            show={showDetailModal}
            profile={selectedProfile}
            onHide={() => setShowDetailModal(false)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ProfileManagement;
